import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { authActions } from "../../../context/actions/authActions";
import useStore from "../../../context/useStore";
import AideNir from "../../common/HorsConnexion/AideNir";
import Bouton from "../../common/HorsConnexion/Bouton";
import CompteurBlocage from "../../common/HorsConnexion/CompteurBlocage";
import { MESSAGE_SERVEUR_INDISPONIBLE } from "../../common/HorsConnexion/horsConnexion";
import MessageEtat from "../../common/HorsConnexion/MessageEtat";
import PageHorsConnexion from "../../common/HorsConnexion/PageHorsConnexion";
import Saisie from "../../common/HorsConnexion/Saisie";
import SaisieNir from "../../common/HorsConnexion/SaisieNir";
import ForgotPassword from "./ForgotPassword";

/**
 * Connexion à l'espace allocataire (gabarit hors connexion de la charte).
 * Même appel qu'avant (authActions.loginUser → POST auth/login/APP avec le NIR
 * et le mot de passe) ; les erreurs de l'API (state.errors) s'affichent dans la
 * carte : login sous le NIR, password sous le mot de passe (champ vidé),
 * nbAttempts en avertissement, timer en blocage temporaire, other en message.
 */
const Login = () => {
  const [state, dispatch] = useStore();
  const actions = authActions(state, dispatch);
  const location = useLocation();
  const [nir, setNir] = useState("");
  const [password, setPassword] = useState("");
  const [forgotPassword, setForgotPassword] = useState(
    !!(location.state && location.state.vue === "oubli")
  );
  const [isLoading, setIsLoading] = useState(false);
  const [soumis, setSoumis] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canLoggin, setCanLoggin] = useState(true);
  const erreurs = state.errors || {};

  // Toute réponse d'erreur (même vide : serveur injoignable) termine l'attente.
  useEffect(() => {
    setIsLoading(false);
    if (state.errors.password) {
      setPassword("");
    }
    if (state.errors.timer) {
      setTimer(parseInt(state.errors.timer, 10));
    }
  }, [state.errors]);

  // Revenir de « mot de passe oublié » lève l'affichage du blocage, comme avant
  // (l'API reste seule juge du blocage réel).
  useEffect(() => {
    setTimer(0);
    setCanLoggin(true);
  }, [forgotPassword]);

  useEffect(() => {
    if (timer > 0) {
      setCanLoggin(false);
    }
  }, [timer]);

  function loginHandle(e) {
    e.preventDefault();
    setIsLoading(true);
    setSoumis(true);
    actions.loginUser({ login: nir, password }, { silencieux: true });
  }

  const bloque = !canLoggin;
  const nbTentatives = Number(erreurs.nbAttempts) || 0;
  const afficherTentatives = !bloque && !isLoading && nbTentatives > 0;
  const afficherAutre =
    !bloque && !isLoading && !afficherTentatives && !erreurs.login && !erreurs.password && !!erreurs.other;
  const serveurMuet = soumis && !isLoading && !bloque && Object.keys(erreurs).length === 0;

  let libelle = "Se connecter";
  if (bloque) libelle = "Connexion indisponible";
  else if (isLoading) libelle = "Connexion en cours…";

  return (
    <PageHorsConnexion>
      {forgotPassword ? (
        <ForgotPassword setForgotPassword={setForgotPassword} nir={nir} />
      ) : (
        <section aria-labelledby="titre-connexion">
          <h1 id="titre-connexion">Connexion</h1>
          <p className="sous-titre">Identifiez-vous avec votre numéro de sécurité sociale.</p>

          <form onSubmit={loginHandle} noValidate>
            <SaisieNir
              id="nir-login-inp"
              value={nir}
              onChange={(e) => setNir(e.target.value)}
              erreur={erreurs.login || null}
              aide={<AideNir />}
              autoFocus
            />
            <Saisie
              id="password"
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              erreur={erreurs.password || null}
              enErreur={afficherTentatives}
              autoComplete="current-password"
            />

            <div className="ligne">
              <span />
              <button type="button" className="lien" onClick={() => setForgotPassword(true)}>
                Mot de passe oublié ?
              </button>
            </div>

            {afficherTentatives && (
              <MessageEtat ton="avert" icone="alerte">
                Identifiants incorrects. Il vous reste <strong>{nbTentatives}</strong>{" "}
                {nbTentatives > 1 ? "tentatives" : "tentative"} avant le blocage temporaire de votre
                compte.
              </MessageEtat>
            )}

            {bloque && (
              <MessageEtat
                ton="danger"
                icone="cadenas"
                complement={<CompteurBlocage secondes={timer} onFin={() => setCanLoggin(true)} />}
              >
                <strong>Accès temporairement bloqué</strong> après plusieurs tentatives de connexion
                infructueuses. Vous pourrez réessayer dans :
              </MessageEtat>
            )}

            {afficherAutre && (
              <MessageEtat ton="danger" icone="alerte">
                {erreurs.other}
              </MessageEtat>
            )}

            {serveurMuet && (
              <MessageEtat ton="danger" icone="alerte">
                {MESSAGE_SERVEUR_INDISPONIBLE}
              </MessageEtat>
            )}

            <Bouton type="submit" charge={isLoading} disabled={bloque}>
              {libelle}
            </Bouton>
          </form>
        </section>
      )}
    </PageHorsConnexion>
  );
};

export default Login;
